import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bitcoin, Banknote, Loader2, Minus, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { ReconOptions } from "@/components/ReconOptions";
import { useCart } from "@/context/CartContext";
import { api, errMsg, money, linePreview } from "@/lib/api";

function CartLine({ line, fee, pct }) {
  const { change, remove } = useCart();
  const p = line.product;
  const pre = linePreview(line, fee);
  const lineDisc = pct && !p.on_sale ? Math.round(pre.unit * line.quantity * pct) / 100 : 0;
  const id = p.id;
  return (
    <div className="rounded-lg border border-border bg-card p-6" data-testid={`cart-line-${id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold">{p.name}</h3>
          <p className="font-mono-data text-sm text-muted-foreground">{p.vial_size} · {money(pre.unit)} each</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="icon" variant="outline" className="h-8 w-8" data-testid={`cart-qty-minus-${id}`}
            onClick={() => line.quantity > 1 && change(id, { quantity: line.quantity - 1 })}><Minus className="h-3 w-3" /></Button>
          <span className="w-8 text-center font-mono-data" data-testid={`cart-qty-${id}`}>{line.quantity}</span>
          <Button size="icon" variant="outline" className="h-8 w-8" data-testid={`cart-qty-plus-${id}`}
            onClick={() => change(id, { quantity: line.quantity + 1 })}><Plus className="h-3 w-3" /></Button>
          <Button size="icon" variant="ghost" className="h-8 w-8 text-red-400" data-testid={`cart-remove-${id}`} onClick={() => remove(id)}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      {p.category === "peptide" && (
        <div className="mt-4">
          <ReconOptions value={line} onChange={(patch) => change(id, patch)} recommendedMl={p.recommended_bac_ml}
            fee={fee} quantity={line.quantity} prefix={`cart-${id}`} />
        </div>
      )}
      <p className="mt-4 text-right font-mono-data text-sm">Line total <span className="text-emerald-400" data-testid={`cart-line-total-${id}`}>{money(pre.total - lineDisc)}</span></p>
    </div>
  );
}

const PayOption = ({ k, icon: Icon, label, active, onClick }) => (
  <button type="button" onClick={onClick} data-testid={`checkout-payment-${k}-option`}
    className={`flex flex-1 items-center gap-2 rounded-md border px-4 py-3 text-sm transition-colors ${active ? (k === "bitcoin" ? "border-orange-400 bg-orange-500/10" : "border-emerald-500 bg-emerald-500/10") : "border-border hover:bg-secondary"}`}>
    <Icon className={`h-4 w-4 ${k === "bitcoin" ? "text-orange-400" : "text-emerald-400"}`} /> {label}
  </button>
);

const badMl = (l) => l.product.category === "peptide" && l.reconstitute && l.bac_option === "custom" &&
  !(Number(l.custom_bac_ml) >= 0.5 && Number(l.custom_bac_ml) <= 10);

export default function Cart() {
  const { items, clear, sync } = useCart();
  const nav = useNavigate();
  const [settings, setSettings] = useState({ reconstitution_fee: 0, bitcoin_address: "", cash_instructions: "" });
  const [method, setMethod] = useState("cash");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoBusy, setPromoBusy] = useState(false);
  const [auto, setAuto] = useState(null);

  const applyPromo = async () => {
    setPromoBusy(true);
    try {
      const { data } = await api.post("/shop/promo/check", { code: promoInput });
      setPromo(data);
      toast.success(`${data.code} applied — ${data.percent}% off`);
    } catch (e) {
      setPromo(null);
      toast.error(errMsg(e));
    } finally {
      setPromoBusy(false);
    }
  };

  useEffect(() => {
    api.get("/shop/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/shop/products").then((r) => sync(r.data)).catch(() => {});
    api.get("/shop/auto-discount").then((r) => setAuto(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fee = settings.reconstitution_fee;
  const previews = items.map((l) => linePreview(l, fee));
  const subtotal = previews.reduce((s, p, i) => s + p.unit * items[i].quantity, 0);
  const reconTotal = previews.reduce((s, p) => s + p.reconFee, 0);
  const active = promo || auto;
  const discountLabel = promo ? promo.code : auto ? `${auto.label} ${auto.percent}%` : "";
  const discount = active
    ? items.reduce((s, l, i) => s + (l.product.on_sale ? 0 : Math.round(previews[i].unit * l.quantity * active.percent) / 100), 0)
    : 0;

  const place = async () => {
    setBusy(true);
    try {
      const payload = {
        payment_method: method, notes, promo_code: promo && discount > 0 ? promo.code : null,
        items: items.map((l) => ({
          product_id: l.product.id, quantity: l.quantity, reconstitute: l.product.category === "peptide" && l.reconstitute,
          bac_option: l.bac_option, custom_bac_ml: l.bac_option === "custom" && l.custom_bac_ml !== "" ? Number(l.custom_bac_ml) : null,
        })),
      };
      const { data } = await api.post("/orders", payload);
      clear();
      toast.success(`Order ${data.order_number} placed`);
      nav("/orders");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!items.length) {
    return (
      <div className="mx-auto max-w-xl px-6 py-24 text-center" data-testid="cart-empty">
        <h1 className="text-3xl font-semibold">Your cart is empty</h1>
        <Button asChild className="mt-6" data-testid="cart-go-shop-button"><Link to="/shop">Browse the catalog</Link></Button>
      </div>
    );
  }

  return (
    <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-4 py-10 sm:px-6 lg:grid-cols-12 lg:py-14">
      <div className="space-y-4 lg:col-span-7">
        <p className="eyebrow">Checkout</p>
        <h1 className="mb-4 text-4xl font-bold">Your cart</h1>
        {items.map((l) => <CartLine key={l.product.id} line={l} fee={fee} pct={active?.percent || 0} />)}
      </div>
      <aside className="lg:col-span-5">
        <div className="sticky top-24 space-y-6 rounded-lg border border-border border-t-2 border-t-emerald-500 bg-card p-6">
          <div>
            <p className="mb-3 text-sm font-medium">Payment method</p>
            <div className="flex gap-2">
              <PayOption k="cash" icon={Banknote} label="Cash" active={method === "cash"} onClick={() => setMethod("cash")} />
              <PayOption k="bitcoin" icon={Bitcoin} label="Bitcoin" active={method === "bitcoin"} onClick={() => setMethod("bitcoin")} />
            </div>
            <p className="mt-3 text-xs text-muted-foreground" data-testid="checkout-payment-instructions">
              {method === "cash" ? settings.cash_instructions : settings.bitcoin_address ? <>Send BTC to <span className="break-all font-mono-data text-orange-300">{settings.bitcoin_address}</span></> : "Bitcoin payment details will be sent to you after your order is confirmed."}
            </p>
          </div>
          <div>
            {auto && (
              <p className={`mb-3 rounded-md border px-3 py-2 text-xs ${promo ? "border-border text-muted-foreground" : "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"}`} data-testid="checkout-auto-discount">
                {promo ? `Using a code replaces your ${auto.label.toLowerCase()} (${auto.percent}% off). Remove the code to use it instead.` : `${auto.label}: ${auto.percent}% off applied automatically · sale items aren't discounted`}
              </p>
            )}
            <p className="mb-2 text-sm font-medium">Discount code</p>
            <div className="flex gap-2">
              <Input value={promoInput} onChange={(e) => setPromoInput(e.target.value.toUpperCase())} placeholder="e.g. SUMMER20" disabled={!!promo}
                className="font-mono-data tracking-widest" data-testid="checkout-promo-input" />
              {promo ? (
                <Button variant="outline" onClick={() => { setPromo(null); setPromoInput(""); }} data-testid="checkout-promo-remove-button">Remove</Button>
              ) : (
                <Button variant="outline" disabled={!promoInput.trim() || promoBusy} onClick={applyPromo} data-testid="checkout-promo-apply-button">Apply</Button>
              )}
            </div>
            {promo && (
              <p className={`mt-2 text-xs ${discount > 0 ? "text-emerald-400" : "text-amber-400"}`} data-testid="checkout-promo-status">
                {discount > 0 ? `${promo.code}: ${promo.percent}% off · sale items and reconstitution fees aren't discounted` : "This code doesn't apply to items already on sale."}
              </p>
            )}
          </div>
          <div>
            <p className="mb-2 text-sm font-medium">Order notes</p>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Pickup time, questions…" data-testid="checkout-notes-input" />
          </div>
          <div className="space-y-2 border-t border-border pt-4 font-mono-data text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Products</span><span data-testid="checkout-subtotal">{money(subtotal)}</span></div>
            {discount > 0 && <div className="flex justify-between text-emerald-400"><span>Discount ({discountLabel})</span><span data-testid="checkout-discount">-{money(discount)}</span></div>}
            <div className="flex justify-between"><span className="text-muted-foreground">Reconstitution fees</span><span data-testid="checkout-recon-fees">{money(reconTotal)}</span></div>
            <div className="flex justify-between pt-2 text-lg"><span>Total</span><span className="text-emerald-400" data-testid="checkout-total">{money(subtotal - discount + reconTotal)}</span></div>
          </div>
          {items.some(badMl) && <p className="text-sm text-amber-400" data-testid="checkout-ml-error">Custom BAC water must be between 0.5 and 10 mL.</p>}
          <Button className="w-full" size="lg" disabled={busy || items.some(badMl)} onClick={place} data-testid="checkout-place-order-button">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Place order
          </Button>
          <p className="text-center text-xs text-muted-foreground">Your order is sent as pending. Payment is collected separately.</p>
        </div>
      </aside>
    </div>
  );
}
